import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Shield,
  AlertTriangle,
  Lock,
  Eye,
  User,
  Clock,
  Database,
  Download,
  RefreshCw,
  Search,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { fetchBlackboxAudit, fetchSentienceEvents } from '../lib/api';
import { safeFallback } from '../lib/production-guard';
import { useDemoMode } from './DemoContext';
import { SAMPLE_DATA_BADGE, sampleDataDisclaimer } from '../lib/disclaimers.generated';

function useSafeDemoMode() {
  try {
    return useDemoMode();
  } catch {
    return { isDemoMode: false, activeScenario: null, scenarios: [], setIsDemoMode: () => {}, setActiveScenario: () => {} };
  }
}

interface TrustChainEvent {
  id: string;
  timestamp: string;
  type: 'access' | 'override' | 'security' | 'deployment' | 'authentication' | 'data_access';
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: string;
  target: string;
  action: string;
  description: string;
  userId?: string;
  personaId?: string;
  ipAddress: string;
  status: 'success' | 'failed' | 'pending' | 'blocked';
  metadata: Record<string, any>;
  chainHash: string;
}

const mockEvents: TrustChainEvent[] = [
  {
    id: 'tc-001',
    timestamp: '2026-05-01T10:47:23Z',
    type: 'override',
    severity: 'high',
    source: 'Admin Console',
    target: 'Executive Agent',
    action: 'PERSONA_OVERRIDE_INITIATED',
    description: 'Executive escalation activated for Alpha-tier agent',
    userId: 'admin-001',
    personaId: 'persona-001',
    ipAddress: '192.168.1.100',
    status: 'success',
    metadata: { reason: 'Emergency protocol activation', approval: 'auto-granted' },
    chainHash: 'a1b2c3d4e5f6789012345678901234567890abcdef'
  },
  {
    id: 'tc-002',
    timestamp: '2026-05-01T10:45:12Z',
    type: 'security',
    severity: 'medium',
    source: 'Security Module',
    target: 'Authentication System',
    action: 'FAILED_LOGIN_ATTEMPT',
    description: 'Multiple failed authentication attempts detected',
    ipAddress: '203.0.113.45',
    status: 'blocked',
    metadata: { attempts: 5, user_agent: 'Unknown', geo_location: 'Unknown' },
    chainHash: 'b2c3d4e5f6789012345678901234567890abcdef1'
  },
  {
    id: 'tc-003',
    timestamp: '2026-05-01T10:42:08Z',
    type: 'deployment',
    severity: 'low',
    source: 'Module Manager',
    target: 'EnterpriseUIModule',
    action: 'MODULE_DEPLOYMENT',
    description: 'New module successfully deployed to production environment',
    userId: 'system',
    ipAddress: '127.0.0.1',
    status: 'success',
    metadata: { version: '1.0.0', environment: 'production', health_check: 'passed' },
    chainHash: 'c3d4e5f6789012345678901234567890abcdef12'
  },
  {
    id: 'tc-004',
    timestamp: '2026-05-01T10:38:45Z',
    type: 'access',
    severity: 'low',
    source: 'Security Panel',
    target: 'Operations Agent',
    action: 'PERSONA_STATUS_QUERY',
    description: 'Agent status requested via security interface',
    userId: 'operator-007',
    personaId: 'persona-002',
    ipAddress: '192.168.1.85',
    status: 'success',
    metadata: { access_level: 80, clearance: 'SECRET' },
    chainHash: 'd4e5f6789012345678901234567890abcdef123'
  },
  {
    id: 'tc-005',
    timestamp: '2026-05-01T10:35:30Z',
    type: 'data_access',
    severity: 'medium',
    source: 'Analytics Engine',
    target: 'System Metrics',
    action: 'SENSITIVE_DATA_ACCESS',
    description: 'Analytics agent accessed classified performance metrics',
    personaId: 'persona-003',
    ipAddress: '192.168.1.120',
    status: 'success',
    metadata: { data_classification: 'CONFIDENTIAL', records_accessed: 2847 },
    chainHash: 'e5f6789012345678901234567890abcdef1234'
  }
];

function generateDemoAuditEvents(scenarioId: string): TrustChainEvent[] {
  const now = new Date();
  const base: TrustChainEvent[] = mockEvents.map(e => ({ ...e }));

  if (scenarioId === 'enterprise-ops') {
    // Enterprise: more routine events, higher volume
    for (let i = 0; i < 15; i++) {
      base.push({
        id: `demo-ent-${i}`,
        timestamp: new Date(now.getTime() - i * 120000).toISOString(),
        type: i % 3 === 0 ? 'deployment' : i % 3 === 1 ? 'access' : 'data_access',
        severity: i % 5 === 0 ? 'medium' : 'low',
        source: 'Enterprise Automation',
        target: `Agent-${String(i + 1).padStart(3, '0')}`,
        action: i % 2 === 0 ? 'MODULE_DEPLOYMENT' : 'ROUTINE_ACCESS',
        description: `Enterprise operations event ${i + 1} -- routine governance activity`,
        ipAddress: '192.168.1.' + (100 + i),
        status: 'success',
        metadata: { environment: 'production', batch: i },
        chainHash: `ent${String(i).padStart(6, '0')}abcdef1234567890abcdef`,
      });
    }
  } else if (scenarioId === 'crisis-simulation') {
    // Crisis: more critical/high severity events
    const crisisEvents: Partial<TrustChainEvent>[] = [
      { type: 'security', severity: 'critical', action: 'BREACH_DETECTED', description: 'CRITICAL: Unauthorized agent activity detected in production cluster', status: 'blocked' },
      { type: 'override', severity: 'critical', action: 'EMERGENCY_OVERRIDE', description: 'Alpha persona emergency override activated -- all agents paused', status: 'success' },
      { type: 'security', severity: 'high', action: 'FAILOVER_INITIATED', description: 'Automatic failover to backup governance chain initiated', status: 'pending' },
      { type: 'authentication', severity: 'high', action: 'FORCED_REAUTH', description: 'Forced re-authentication for all active sessions', status: 'success' },
      { type: 'security', severity: 'critical', action: 'QUARANTINE_AGENT', description: 'Rogue agent quarantined -- behavioral drift exceeded threshold', status: 'success' },
      { type: 'override', severity: 'high', action: 'KILL_SWITCH_ARMED', description: 'Fleet kill switch armed pending operator confirmation', status: 'pending' },
      { type: 'security', severity: 'medium', action: 'INCIDENT_LOGGED', description: 'Incident report generated and forwarded to response team', status: 'success' },
      { type: 'data_access', severity: 'high', action: 'FORENSIC_SNAPSHOT', description: 'Forensic memory snapshot captured for all active agents', status: 'success' },
    ];
    crisisEvents.forEach((evt, i) => {
      base.push({
        id: `demo-crisis-${i}`,
        timestamp: new Date(now.getTime() - i * 60000).toISOString(),
        type: evt.type as any || 'security',
        severity: evt.severity as any || 'high',
        source: 'Crisis Response System',
        target: 'Governance Fleet',
        action: evt.action || 'CRISIS_EVENT',
        description: evt.description || 'Crisis event',
        ipAddress: '10.0.0.' + (1 + i),
        status: evt.status as any || 'success',
        metadata: { crisisLevel: 'ALPHA', responseTeam: 'active' },
        chainHash: `crisis${String(i).padStart(4, '0')}abcdef1234567890abcdef`,
      });
    });
  } else if (scenarioId === 'growth-showcase') {
    // Growth: onboarding and scaling events
    for (let i = 0; i < 8; i++) {
      base.push({
        id: `demo-growth-${i}`,
        timestamp: new Date(now.getTime() - i * 180000).toISOString(),
        type: i % 2 === 0 ? 'deployment' : 'access',
        severity: 'low',
        source: 'Scaling Engine',
        target: `New-Agent-${i + 1}`,
        action: i % 2 === 0 ? 'AGENT_ONBOARDED' : 'WORKFLOW_DEPLOYED',
        description: `Growth event -- ${i % 2 === 0 ? 'new agent onboarded to fleet' : 'automation workflow deployed'}`,
        ipAddress: '172.16.0.' + (10 + i),
        status: 'success',
        metadata: { tier: 'professional', scalePhase: i < 4 ? 'startup' : 'growth' },
        chainHash: `growth${String(i).padStart(4, '0')}abcdef1234567890abcdef`,
      });
    }
  }

  return base.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 50);
}

// Auto-scrolling container for the audit feed
function AuditFeedScroller({ children, filteredEvents, isLiveMode }: { children: React.ReactNode; filteredEvents: any[]; isLiveMode: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLiveMode || !containerRef.current) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    containerRef.current.scrollTo({
      top: 0,
      behavior: prefersReduced ? 'auto' : 'smooth',
    });
  }, [filteredEvents, isLiveMode]);

  return (
    <div ref={containerRef} className="max-h-96 overflow-y-auto" style={{ scrollBehavior: 'smooth' }}>
      {children}
    </div>
  );
}

// Severity dot color mapping for pulse indicator
const SEVERITY_DOT_COLOR: Record<string, string> = {
  critical: '#F87171',
  high: '#fb923c',
  medium: '#FBBF24',
  low: '#34D399',
};

// Animated event row: slides in from top, dot pulses once on mount
function AnimatedEventRow({ children, severity }: { children: React.ReactNode; severity: string }) {
  const [mounted, setMounted] = useState(false);
  const dotColor = SEVERITY_DOT_COLOR[severity] || '#9ca3af';

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) { setMounted(true); return; }
    // Small delay so the browser can paint the initial state
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      <span
        className="audit-event-dot"
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          backgroundColor: dotColor,
          flexShrink: 0,
          marginTop: '18px',
          marginLeft: '8px',
          marginRight: '-2px',
          animation: mounted ? 'none' : 'auditDotPulse 0.6s ease-out',
        }}
      />
      <div
        style={{
          flex: 1,
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(-12px)',
          transition: 'opacity 0.35s ease, transform 0.35s ease',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function TrustChainAuditFeed() {
  const [events, setEvents] = useState<TrustChainEvent[]>(safeFallback(mockEvents) || []);
  const [filteredEvents, setFilteredEvents] = useState<TrustChainEvent[]>(safeFallback(mockEvents) || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [isLiveMode, setIsLiveMode] = useState(true);
  const [isUsingDemoData, setIsUsingDemoData] = useState(true);
  const { isDemoMode: globalDemoMode, activeScenario } = useSafeDemoMode();

  // Apply demo scenario events when global demo mode changes
  useEffect(() => {
    if (globalDemoMode && activeScenario) {
      const demoEvents = generateDemoAuditEvents(activeScenario.id);
      setEvents(demoEvents);
      setIsUsingDemoData(true);
    }
  }, [globalDemoMode, activeScenario]);

  // Load audit events from API
  useEffect(() => {
    if (globalDemoMode) return; // Skip API polling during demo mode
    const loadAuditData = async () => {
      const [auditData, sentienceData] = await Promise.all([
        fetchBlackboxAudit(),
        fetchSentienceEvents(),
      ]);

      const auditEntries: TrustChainEvent[] = [];

      if (auditData?.audit && Array.isArray(auditData.audit)) {
        auditData.audit.forEach((entry: any, idx: number) => {
          auditEntries.push({
            id: entry.id || `audit-${idx}`,
            timestamp: entry.timestamp || new Date().toISOString(),
            type: entry.type || 'access',
            severity: entry.severity || 'low',
            source: entry.source || 'Audit System',
            target: entry.target || 'System',
            action: entry.action || 'AUDIT_EVENT',
            description: entry.description || entry.message || 'Audit event recorded',
            userId: entry.userId,
            personaId: entry.personaId,
            ipAddress: entry.ipAddress || '127.0.0.1',
            status: entry.status || 'success',
            metadata: entry.metadata || {},
            chainHash: entry.chainHash || entry.hash || String(idx),
          });
        });
      }

      if (sentienceData?.events && Array.isArray(sentienceData.events)) {
        sentienceData.events.forEach((entry: any, idx: number) => {
          auditEntries.push({
            id: entry.id || `sentience-${idx}`,
            timestamp: entry.timestamp || new Date().toISOString(),
            type: 'security',
            severity: entry.severity || 'high',
            source: 'Sentience Monitor',
            target: entry.target || 'System',
            action: entry.action || 'SENTIENCE_EVENT',
            description: entry.description || entry.message || 'Sentience event detected',
            ipAddress: entry.ipAddress || '127.0.0.1',
            status: entry.status || 'success',
            metadata: entry.metadata || {},
            chainHash: entry.chainHash || entry.hash || String(idx),
          });
        });
      }

      if (auditEntries.length > 0) {
        auditEntries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setEvents(auditEntries.slice(0, 50));
        setIsUsingDemoData(false);
      }
    };

    loadAuditData();
    const interval = setInterval(loadAuditData, 15000);
    return () => clearInterval(interval);
  }, [globalDemoMode]);

  // Filter events based on search and filters
  useEffect(() => {
    let filtered = events;

    if (searchTerm) {
      filtered = filtered.filter(event => 
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.source.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.target.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(event => event.type === typeFilter);
    }

    if (severityFilter !== 'all') {
      filtered = filtered.filter(event => event.severity === severityFilter);
    }

    setFilteredEvents(filtered);
  }, [events, searchTerm, typeFilter, severityFilter]);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-ag-critical bg-red-900 border-red-600';
      case 'high':
        return 'text-orange-400 bg-orange-900 border-orange-600';
      case 'medium':
        return 'text-ag-warning bg-yellow-900 border-yellow-600';
      case 'low':
        return 'text-ag-active bg-green-900 border-green-600';
      default:
        return 'text-[#A8A29E] bg-gray-900 border-[#4A443D]';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'override':
        return <AlertTriangle className="w-4 h-4" />;
      case 'security':
        return <Shield className="w-4 h-4" />;
      case 'access':
        return <Eye className="w-4 h-4" />;
      case 'deployment':
        return <Database className="w-4 h-4" />;
      case 'authentication':
        return <Lock className="w-4 h-4" />;
      case 'data_access':
        return <User className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-ag-active" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-ag-critical" />;
      case 'blocked':
        return <Shield className="w-4 h-4 text-ag-critical" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-ag-warning" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-[#A8A29E]" />;
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const eventTime = new Date(timestamp);
    const diffMs = now.getTime() - eventTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  const exportEvents = () => {
    const dataStr = JSON.stringify(filteredEvents, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `trustchain-audit-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <div className="space-y-6">
      {globalDemoMode && (
        <p className="text-xs text-gray-500 italic">Every governance event is hash-linked and independently verifiable. This feed shows the last 50 events.</p>
      )}
      {/* Controls Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
            <Input
              placeholder="Search audit events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64 bg-gray-900 border-[#4A443D] text-white"
            />
          </div>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-gray-900 border border-[#4A443D] rounded px-3 py-2 text-white text-sm"
          >
            <option value="all">All Types</option>
            <option value="override">Override</option>
            <option value="security">Security</option>
            <option value="access">Access</option>
            <option value="deployment">Deployment</option>
            <option value="authentication">Authentication</option>
            <option value="data_access">Data Access</option>
          </select>
          
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="bg-gray-900 border border-[#4A443D] rounded px-3 py-2 text-white text-sm"
          >
            <option value="all">All Severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant={isLiveMode ? "default" : "outline"}
            size="sm"
            onClick={() => setIsLiveMode(!isLiveMode)}
            className={isLiveMode ? "bg-green-600 hover:bg-green-700" : "border-green-600 text-green-400 hover:bg-green-900"}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${isLiveMode ? 'animate-spin' : ''}`} />
            Live {isLiveMode ? 'ON' : 'OFF'}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={exportEvents}
            className="border-cyan-600 text-cyan-400 hover:bg-cyan-900"
          >
            <Download className="w-4 h-4 mr-1" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Total Events</p>
                <p className="text-2xl font-bold text-cyan-400">{events.length}</p>
              </div>
              <Database className="w-8 h-8 text-cyan-400 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">High/Critical</p>
                <p className="text-2xl font-bold text-ag-critical">
                  {events.filter(e => e.severity === 'high' || e.severity === 'critical').length}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-ag-critical opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Failed Events</p>
                <p className="text-2xl font-bold text-orange-400">
                  {events.filter(e => e.status === 'failed' || e.status === 'blocked').length}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-orange-400 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Last 1h</p>
                <p className="text-2xl font-bold text-green-400">
                  {events.filter(e => new Date().getTime() - new Date(e.timestamp).getTime() < 3600000).length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-green-400 opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Events Feed */}
      <Card className="bg-gray-900 border-[#3A352F]">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Audit Trail Feed{isUsingDemoData && <span className="text-xs font-normal text-amber-400 ml-2" title={sampleDataDisclaimer("dashboard")}>({SAMPLE_DATA_BADGE})</span>}</span>
            <Badge variant="outline" className="text-cyan-400 border-cyan-400">
              {filteredEvents.length} Events
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <style>{`
            @keyframes auditDotPulse {
              0% { transform: scale(1); opacity: 1; }
              50% { transform: scale(2); opacity: 0.6; }
              100% { transform: scale(1); opacity: 1; }
            }
            @media (prefers-reduced-motion: reduce) {
              .audit-event-dot { animation: none !important; }
            }
          `}</style>
          <AuditFeedScroller filteredEvents={filteredEvents} isLiveMode={isLiveMode}>
            {filteredEvents.length === 0 ? (
              <div className="p-12 text-center">
                <Shield className="w-12 h-12 text-[#8F857B] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#A8A29E] mb-2">No audit events recorded yet</h3>
                <p className="text-gray-500">Events will appear here when audit data is available</p>
              </div>
            ) : (
              <div className="space-y-0">
                {filteredEvents.map((event, index) => (
                  <AnimatedEventRow key={event.id} severity={event.severity}>
                  <div
                    className={`p-4 border-b border-[#3A352F] hover:bg-[#292524] transition-colors ${
                      index === 0 && isLiveMode ? 'bg-[#292524] border-l-4 border-l-cyan-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="flex-shrink-0 mt-1">
                          {getTypeIcon(event.type)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-sm font-semibold text-white truncate">
                              {event.action.replace(/_/g, ' ')}
                            </h4>
                            <Badge className={`${getSeverityColor(event.severity)} text-xs`}>
                              {event.severity.toUpperCase()}
                            </Badge>
                            {getStatusIcon(event.status)}
                          </div>
                          
                          <p className="text-[#A8A29E] text-sm mb-2">{event.description}</p>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-[#A8A29E]">
                            <div>
                              <span className="font-medium">Source:</span> {event.source}
                            </div>
                            <div>
                              <span className="font-medium">Target:</span> {event.target}
                            </div>
                            <div>
                              <span className="font-medium">IP:</span> {event.ipAddress}
                            </div>
                            <div>
                              <span className="font-medium">Verification:</span>
                              <code className="ml-1 text-cyan-400">{event.chainHash.substring(0, 8)}...</code>
                            </div>
                          </div>
                          
                          {event.metadata && Object.keys(event.metadata).length > 0 && (
                            <div className="mt-2 text-xs">
                              <span className="font-medium text-gray-500">Metadata: </span>
                              {Object.entries(event.metadata).slice(0, 2).map(([key, value]) => (
                                <span key={key} className="text-[#A8A29E] mr-3">
                                  {key}: <code className="text-cyan-400">{String(value)}</code>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right text-xs text-gray-500 ml-4 flex-shrink-0">
                        <div>{formatTimeAgo(event.timestamp)}</div>
                        <div className="mt-1">{new Date(event.timestamp).toLocaleTimeString()}</div>
                      </div>
                    </div>
                  </div>
                  </AnimatedEventRow>
                ))}
              </div>
            )}
          </AuditFeedScroller>
        </CardContent>
      </Card>
    </div>
  );
}